/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 17-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Right pane — full task field editor for the Task Builder.
 */

import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { Task, TaskAction, TASK_TYPES, RESOURCE_GROUPS_DEFAULT, RESOURCE_TYPES, ResourceType, AseVersion, TASK_ACTION_TYPES } from '../types';
import { deriveParentUid, buildDepGraph, canReach, findCyclePath, isMilestoneUid, computeEstimate, taskDepth } from '../model';
import { fetchTemplateCatalog } from '../../../communications/MessageTemplateService';
import { TemplateSummary } from '../../../communications/CommunicationTypes';
import { fetchPlanMasters } from '../../../automation/AutomationMasterService';
import { PlanMasterSummaryDTO } from '../../../automation/AutomationTypes';

export interface TaskInspectorProps {
  task: Task | null;
  tasks: Task[]; // full set of valid tasks, for dependency + parent lookups
  onUpdate: (uid: string, patch: Partial<Task>, renameFrom?: string) => void;
  /** ASE 1.0 hides the 2.0-only scheduling editors (workers/compression/dep-class/system-task). */
  aseVersion?: AseVersion;
}

/** Right pane: full task field editor. WBS = task_uid (editable, drives hierarchy). */
export default function TaskInspector({ task, tasks, onUpdate, aseVersion = '2.0' }: TaskInspectorProps) {
  const is2 = aseVersion === '2.0';
  const [uidDraft, setUidDraft] = useState(task?.task_uid ?? '');
  useEffect(() => setUidDraft(task?.task_uid ?? ''), [task?.task_uid]);
  // Build the dependency graph ONCE per task-set change, not once per rendered dependency row.
  const depGraph = useMemo(() => buildDepGraph(tasks), [tasks]);

  // AB#26892 — 'Send eMail' template picker (design doc §5.2 item 4, corrected 2026-08-09).
  // GET /api/v1/message-templates/catalog?channel=EMAIL is genuinely shared/platform-wide (unlike
  // SMS templates or automation plans, both tenant-owned) — a real, meaningful catalog to pick
  // from here, even though this component authors a template with no bound tenant/project. No
  // preview: that needs a real project's real customer data, which does not exist at authoring
  // time (see TaskAction's own javadoc in types.ts for the full reasoning).
  const { accessToken } = useOidcAccessToken();
  const [emailTemplates, setEmailTemplates] = useState<TemplateSummary[]>([]);
  useEffect(() => {
    if (!accessToken) {
      return;
    }
    fetchTemplateCatalog('EMAIL', accessToken).then((res) => {
      if (res.kind === 'ok') {
        setEmailTemplates(res.data);
      }
    });
  }, [accessToken]);

  // AB#26892/26913 — 'Start/End Automation plan' master picker. Unlike automationPlanId (a
  // TENANT-scope fork's own uid — meaningless in a shared template, since each tenant's fork of
  // "the same" plan has a DIFFERENT uid), automationPlanMasterUid is the provider master's own
  // stable uid: the SAME value for every tenant, resolved per-tenant into that tenant's own fork
  // at Create-Bid/Create-Estimate time (TradeTemplateLoader._resolve_task_actions, ASE 2.0). This
  // catalog read is genuinely meaningful here — masters are provider-owned, same tier as this
  // template — unlike the tenant-owned automationPlanId the old field asked authors to guess at.
  // PUBLISHED only: a DRAFT/RETIRED master has no forkable version for a tenant to resolve against.
  const [planMasters, setPlanMasters] = useState<PlanMasterSummaryDTO[]>([]);
  useEffect(() => {
    if (!accessToken) {
      return;
    }
    fetchPlanMasters(accessToken).then((res) => {
      if (res.kind === 'ok') {
        setPlanMasters(res.data.filter((m) => m.status === 'PUBLISHED'));
      }
    });
  }, [accessToken]);

  if (!task) {
    return (
      <Box sx={{ width: '100%', height: '100%', borderLeft: 1, borderColor: 'divider', display: 'flex', alignItems: 'center',
        justifyContent: 'center', color: 'text.disabled', p: 2.5, textAlign: 'center', fontSize: 13 }}>
        Select a task to edit its details
      </Box>
    );
  }

  const set = (patch: Partial<Task>) => onUpdate(task.task_uid, patch);
  // Numeric setter that never writes NaN into the stored task (invalid keystrokes are ignored; a blank
  // field coerces to 0 as before). Prevents `+e.target.value` NaN from leaking into task JSON/totals.
  // `min` clamps out negative input (typing "-1" or spinning below the floor) — the HTML `min` attribute
  // on the field only affects the spinner arrows, not manual typing, so the floor is enforced here too.
  const setNum = (key: keyof Task, raw: string, min = 0) => {
    const n = Number(raw);
    if (Number.isFinite(n)) set({ [key]: Math.max(min, n) } as Partial<Task>);
  };
  const otherTasks = tasks.filter(t => t.task_uid !== task.task_uid && t.valid !== false);
  // A parent's timing is DERIVED from its children — never directly editable (see computeEstimate).
  const isParent = isMilestoneUid(tasks, task.task_uid);
  const derivedEstimate = isParent ? computeEstimate(tasks, task.task_uid) : null;
  // A phase root (depth 0, e.g. "1") is never renumberable — the 4 phases must always exist at their
  // fixed top-level slot. Its own WBS field is locked; and no OTHER task may be rewritten into a bare
  // (dot-less) number either, since that would mint a disconnected new "phase" entry.
  const isRoot = taskDepth(task.task_uid) === 0;
  const depSet = new Set(task.dependencies);
  const uidValid = /^\d+\.\d+(\.\d+)*$/.test(uidDraft);
  const uidTaken = uidDraft !== task.task_uid && tasks.some(t => t.task_uid === uidDraft);
  const parentUidOfDraft = deriveParentUid(uidDraft);
  const parentMissing = !!parentUidOfDraft && !tasks.some(t => t.task_uid === parentUidOfDraft && t.task_uid !== task.task_uid);

  function commitUid() {
    if (uidValid && !uidTaken && !parentMissing && uidDraft !== task!.task_uid) {
      onUpdate(task!.task_uid, { task_uid: uidDraft, parent_task_uid: parentUidOfDraft }, task!.task_uid);
    } else setUidDraft(task!.task_uid);
  }

  return (
    <Box sx={{ width: '100%', height: '100%', borderLeft: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.disabled">TASK {task.task_uid}</Typography>
        <Typography variant="subtitle1" fontWeight={700}>{task.taskName}</Typography>
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 2 }}>

        <Accordion defaultExpanded elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Identity</AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField label="WBS number" value={uidDraft} onChange={e => setUidDraft(e.target.value)} onBlur={commitUid}
                error={!isRoot && (!uidValid || uidTaken || parentMissing)} size="small" fullWidth
                disabled={isRoot}
                helperText={isRoot ? 'Phase root — fixed, cannot be renumbered'
                  : !uidValid ? 'Use digits separated by dots, e.g. 1.2.3' : uidTaken ? 'Already in use' : parentMissing ? `Parent "${parentUidOfDraft}" doesn't exist` : ' '}
                sx={{ '& input': { fontFamily: 'monospace' } }} />
              <TextField label="Parent" value={parentUidOfDraft || '— (top level)'} size="small" fullWidth disabled />
            </Box>
            <TextField label="Task name" value={task.taskName} onChange={e => set({ taskName: e.target.value })} size="small" fullWidth sx={{ mb: 1.5 }} />
            <TextField select label="Task type" value={task.taskType} onChange={e => set({ taskType: e.target.value as Task['taskType'] })} size="small" fullWidth>
              {TASK_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            {is2 && TASK_TYPES.find(t => t.value === task.taskType)?.system && <Chip sx={{ mt: 1 }} size="small" color="success" label="System-generated by this task type" />}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Timing &amp; workforce</AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
              <TextField label="Est. days" type="number" size="small" fullWidth inputProps={{ min: 0 }}
                value={isParent ? derivedEstimate!.days : task.estimatedDays}
                onChange={e => setNum('estimatedDays', e.target.value)}
                disabled={isParent}
                helperText={isParent ? 'Sum of child tasks' : ' '} />
              <TextField label="Est. hours" type="number" size="small" fullWidth inputProps={{ min: 0 }}
                value={isParent ? derivedEstimate!.hours : task.estimatedHours}
                onChange={e => setNum('estimatedHours', e.target.value)}
                disabled={isParent}
                helperText={isParent ? 'Sum of child tasks' : ' '} />
            </Box>
            {is2 && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <TextField label="Min workers" type="number" value={task.min_workers} onChange={e => setNum('min_workers', e.target.value, 1)} size="small" fullWidth inputProps={{ min: 1 }} />
                  <TextField label="Max workers" type="number" value={task.max_workers} onChange={e => setNum('max_workers', e.target.value, 1)} size="small" fullWidth inputProps={{ min: 1 }} />
                </Box>
                <FormControlLabel control={<Switch checked={task.is_compressible} onChange={e => set({ is_compressible: e.target.checked })} />} label="Compressible (can overlap crews)" />
                <TextField label="Compression factor" type="number" value={task.compression_factor}
                  onChange={e => setNum('compression_factor', e.target.value)} size="small" fullWidth sx={{ mt: 1.5, mb: 1.5 }}
                  inputProps={{ step: 0.1, min: 0 }} disabled={!task.is_compressible}
                  helperText={task.is_compressible ? 'How much this task can overlap (1 = none)' : 'Enable “Compressible” to set'} />
              </>
            )}
            <TextField label="Timeline" value={task.timeLine} onChange={e => set({ timeLine: e.target.value })} size="small" fullWidth
              helperText="Optional legacy schedule hint" />
            {/* is_milestone is derived from the hierarchy (children) and written at save.
                is_system_task is NOT derived — it is persisted verbatim from the template. */}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Dependencies</AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Task must wait on:</Typography>
            <Box sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {otherTasks.map(o => {
                const checked = depSet.has(o.task_order);
                const cls = task.dependency_classes[o.task_order] ?? 'HARD';
                const cyclic = checked && canReach(depGraph, o.task_order, task.task_order);
                const cycleMsg = cyclic ? `Cyclic dependency: ${findCyclePath(tasks, task.task_order, o.task_order).join(' → ')}. Please remove a link in this chain.` : '';
                return (
                  <Box key={o.task_uid} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
                    bgcolor: cyclic ? (theme => alpha(theme.palette.error.main, 0.08)) : 'transparent' }}>
                    <Checkbox size="small" checked={checked} onChange={e => {
                      const deps = new Set(task.dependencies);
                      const dc = { ...task.dependency_classes };
                      if (e.target.checked) { deps.add(o.task_order); dc[o.task_order] = 'HARD'; }
                      else { deps.delete(o.task_order); delete dc[o.task_order]; }
                      set({ dependencies: Array.from(deps), dependency_classes: dc });
                    }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1, color: cyclic ? 'error.main' : 'text.primary', fontWeight: cyclic ? 600 : 400 }}>{o.taskName}</Typography>
                    {cyclic && <Tooltip title={cycleMsg}><WarningAmberIcon fontSize="small" color="error" /></Tooltip>}
                    {checked && is2 && (
                      <Chip size="small" onClick={() => set({ dependency_classes: { ...task.dependency_classes, [o.task_order]: cls === 'HARD' ? 'SOFT' : 'HARD' } })}
                        color={cls === 'HARD' ? 'error' : 'info'} label={cls} />
                    )}
                  </Box>
                );
              })}
            </Box>
            {otherTasks.some(o => depSet.has(o.task_order) && canReach(depGraph, o.task_order, task.task_order)) && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>⚠ One or more dependencies above create a cycle — fix the highlighted rows.</Typography>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Resources</AccordionSummary>
          <AccordionDetails>
            {task.resources.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center' }}>
                <TextField select size="small" value={r.type} sx={{ flex: 1 }}
                  onChange={e => { const list = [...task.resources]; list[i] = { ...r, type: e.target.value as ResourceType }; set({ resources: list }); }}>
                  {RESOURCE_TYPES.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                </TextField>
                <TextField select size="small" value={r.group} sx={{ flex: 1.3 }}
                  onChange={e => { const list = [...task.resources]; list[i] = { ...r, group: e.target.value }; set({ resources: list }); }}>
                  {RESOURCE_GROUPS_DEFAULT.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                </TextField>
                <TextField size="small" type="number" value={r.count} sx={{ width: 64 }} inputProps={{ min: 1 }}
                  onChange={e => {
                    const n = Number(e.target.value);
                    if (!Number.isFinite(n)) return;
                    const list = [...task.resources];
                    list[i] = { ...r, count: Math.max(1, n) };
                    set({ resources: list });
                  }} />
                <IconButton size="small" onClick={() => set({ resources: task.resources.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => set({ resources: [...task.resources, { type: '1099', group: 'labour', count: 1 }] })}>Add resource</Button>
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Actions</AccordionSummary>
          <AccordionDetails>
            {task.actions.map((a, i) => (
              <Box key={i} data-testid={`action-row-${i}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center' }}>
                  <TextField select size="small" value={a.state} sx={{ flex: 1 }}
                    onChange={e => { const list = [...task.actions]; list[i] = { ...a, state: e.target.value as TaskAction['state'] }; set({ actions: list }); }}>
                    {/* Same legacy-value-preservation shape as the `action` field below: an
                        already-saved row using the retired 'On Complete' spelling must still
                        display (and round-trip untouched unless deliberately changed), not render
                        blank because it no longer matches either curated MenuItem. */}
                    {(a.state as string) === 'On Complete' && (
                      <MenuItem value="On Complete"><em>On Complete (legacy)</em></MenuItem>
                    )}
                    <MenuItem value="On Start">On Start</MenuItem>
                    <MenuItem value="On Completion">On Completion</MenuItem>
                  </TextField>
                  <TextField select size="small" value={a.action} sx={{ flex: 1.4 }}
                    onChange={e => { const list = [...task.actions]; list[i] = { ...a, action: e.target.value }; set({ actions: list }); }}>
                    {/* a.action is a free string with no backend enum — an older task's value that
                        predates TASK_ACTION_TYPES (e.g. a legacy "Send Contract") won't match any of
                        the curated MenuItems below, and MUI's Select renders BLANK when the current
                        value matches nothing, silently hiding real data from the user. Inject it as
                        its own option so it displays (and round-trips untouched unless deliberately
                        changed) instead of looking like the action was cleared. */}
                    {a.action && !TASK_ACTION_TYPES.includes(a.action) && (
                      <MenuItem value={a.action}><em>{a.action} (legacy)</em></MenuItem>
                    )}
                    {TASK_ACTION_TYPES.map(x => <MenuItem key={x} value={x}>{x}</MenuItem>)}
                  </TextField>
                  <IconButton size="small" onClick={() => set({ actions: task.actions.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
                </Box>
                {(a.action === 'Start Automation Plan' || a.action === 'End Automation Plan') && (
                  <>
                    {/* resolve-plan-from-metakey-feature-spec.md §2/§3: opt-in per-row flag. When on,
                        the tenant-side reconciler prefers this task's own additionalData.metaKey's
                        resolved value (a Populate-pattern MethodDef authored in the Mapping tab) over
                        the static automationPlanMasterUid below, which becomes purely the fallback. */}
                    <FormControlLabel sx={{ mb: 0.5 }}
                      control={
                        <Switch checked={!!a.resolvePlanFromOwnMetaKey}
                          onChange={e => { const list = [...task.actions]; list[i] = { ...a, resolvePlanFromOwnMetaKey: e.target.checked }; set({ actions: list }); }} />
                      }
                      label="Resolve plan from this task's own MetaKey" />
                    {/* design doc §10.1 + AB#26913 correction: these two action types resolve a plan
                        via `automationPlanMasterUid` (the provider master's stable, cross-tenant uid),
                        NOT `item` and NOT a literal `automationPlanId` — this template has no bound
                        tenant, so there is no tenant-specific fork uid to pick. Resolution to the
                        uploading tenant's own fork happens later, at Create-Bid/Create-Estimate time. */}
                    <TextField select label="Automation plan" size="small" fullWidth value={a.automationPlanMasterUid ?? ''}
                      onChange={e => { const list = [...task.actions]; list[i] = { ...a, automationPlanMasterUid: e.target.value }; set({ actions: list }); }}
                      helperText={
                        a.resolvePlanFromOwnMetaKey
                          ? "Used only as a fallback — this task's MetaKey resolved value takes priority when one exists"
                          : a.automationPlanId && !a.automationPlanMasterUid
                            ? 'Legacy literal plan id on this row — kept as-is; pick a plan here to switch it to the resolvable form'
                            : 'A tenant using this template must have forked this plan for the action to fire'
                      }>
                      <MenuItem value=""><em>None selected</em></MenuItem>
                      {a.automationPlanMasterUid && !planMasters.some(m => m.planMasterUid === a.automationPlanMasterUid) && (
                        <MenuItem value={a.automationPlanMasterUid}><em>{a.automationPlanMasterUid} (not in the current catalog)</em></MenuItem>
                      )}
                      {planMasters.map(m => (
                        <MenuItem key={m.planMasterUid} value={m.planMasterUid}>{m.name ?? m.planMasterUid}</MenuItem>
                      ))}
                    </TextField>
                  </>
                )}
                {a.action === 'Send eMail' && (
                  // design doc §5.2 item 4, corrected 2026-08-09: message_template_master is
                  // genuinely shared/platform-wide (unlike SMS templates or plans), so a real
                  // picker over live templates works here — `item` stores the templateMasterUid,
                  // same convention SendEmailActionHandler already expects.
                  <TextField select label="Email template" size="small" fullWidth value={a.item ?? ''}
                    onChange={e => { const list = [...task.actions]; list[i] = { ...a, item: e.target.value }; set({ actions: list }); }}
                    helperText="Live, approved template catalog — no preview here (no bound project yet)">
                    <MenuItem value=""><em>None selected</em></MenuItem>
                    {a.item && !emailTemplates.some(t => t.templateMasterUid === a.item) && (
                      <MenuItem value={a.item}><em>{a.item} (not in the current catalog)</em></MenuItem>
                    )}
                    {emailTemplates.map(t => (
                      <MenuItem key={t.templateMasterUid} value={t.templateMasterUid}>{t.name}</MenuItem>
                    ))}
                  </TextField>
                )}
                {a.action !== 'Start Automation Plan' && a.action !== 'End Automation Plan' && a.action !== 'Send eMail' && (
                  <TextField label="Item" size="small" fullWidth value={a.item ?? ''}
                    onChange={e => { const list = [...task.actions]; list[i] = { ...a, item: e.target.value }; set({ actions: list }); }}
                    helperText="Optional target/item this action applies to" />
                )}
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={() => set({ actions: [...task.actions, { state: 'On Start', action: TASK_ACTION_TYPES[0], item: '' }] })}>Add action</Button>
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Checklist</AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Field-crew sub-steps shown under this task.</Typography>
            {task.checklistItems.map((c, i) => (
              <Box key={i} data-testid={`checklist-item-${i}`} sx={{ display: 'flex', gap: 0.75, mb: 0.75, alignItems: 'center' }}>
                <Typography sx={{ width: 18, flexShrink: 0, color: 'text.disabled', fontSize: 12 }}>{i + 1}.</Typography>
                <TextField size="small" fullWidth value={c.label}
                  onChange={e => { const list = [...task.checklistItems]; list[i] = { ...c, label: e.target.value }; set({ checklistItems: list }); }} />
                <IconButton size="small" onClick={() => set({ checklistItems: task.checklistItems.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />}
              onClick={() => set({ checklistItems: [...task.checklistItems, { label: '', order: task.checklistItems.length + 1 }] })}>
              Add checklist item
            </Button>
          </AccordionDetails>
        </Accordion>

        <Accordion elevation={0} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Metadata &amp; mapping</AccordionSummary>
          <AccordionDetails>
            <TextField label="Meta key" size="small" fullWidth sx={{ mb: 1.5 }}
              value={String(task.additionalData?.metaKey ?? '')}
              onChange={e => set({ additionalData: setAdditional(task.additionalData, 'metaKey', e.target.value) })}
              helperText="Links this task to a mapped question (SDM ItemDef)" />
            <TextField label="Unit" size="small" fullWidth
              value={String(task.additionalData?.unit ?? '')}
              onChange={e => set({ additionalData: setAdditional(task.additionalData, 'unit', e.target.value) })}
              helperText="Unit of measure for this task, when applicable" />
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}

/** Set/clear a key on additionalData without leaving empty strings behind (keeps stored JSON clean). */
function setAdditional(current: Record<string, unknown>, key: string, value: string): Record<string, unknown> {
  const next = { ...(current ?? {}) };
  if (value === '') delete next[key];
  else next[key] = value;
  return next;
}
